/**
 * Nomes de países e idiomas no idioma da interface.
 *
 * As listas `shared/constants/countries.js` e
 * `dashboard/components/widgets/conversation/advancedFilterItems/languages.js`
 * guardam o nome em inglês como valor CANÔNICO — é ele que já foi gravado em
 * `contact.additional_attributes.country` de registros antigos, e que casa os
 * filtros salvos. Mexer nele mudaria dado, não tradução. Por isso a tradução
 * acontece só na exibição.
 *
 * A tradução usa `Intl.DisplayNames`, que vem do próprio navegador: ao habilitar
 * um novo idioma em `shared/constants/enabledLocales.js` os nomes de país e de
 * idioma passam a sair traduzidos sozinhos, sem nenhum catálogo para manter.
 * Se o navegador não souber o código (ou não suportar a API), cai no nome
 * canônico em inglês — nunca em texto vazio.
 */
import { DEFAULT_LOCALE } from 'shared/constants/enabledLocales';

/** Converte o formato interno ('pt_BR') para BCP 47 ('pt-BR'). */
const toBcp47 = locale => String(locale || DEFAULT_LOCALE).replace(/_/g, '-');

const formatterCache = new Map();

const getFormatter = (type, locale) => {
  const key = `${type}:${locale}`;
  if (formatterCache.has(key)) return formatterCache.get(key);
  let formatter = null;
  try {
    formatter = new Intl.DisplayNames([toBcp47(locale)], {
      type,
      fallback: 'none',
    });
  } catch {
    formatter = null;
  }
  formatterCache.set(key, formatter);
  return formatter;
};

/** Primeira letra maiúscula: o CLDR devolve idiomas em minúscula ('português'). */
const capitalize = value =>
  typeof value === 'string' && value
    ? value.charAt(0).toLocaleUpperCase() + value.slice(1)
    : value;

const displayName = (type, code, locale, fallback) => {
  if (!code) return fallback || '';
  const formatter = getFormatter(type, locale);
  if (!formatter) return fallback || code;
  try {
    // Codigos internos usam '_' ('pt_BR', 'zh_Hant_TW'); o Intl exige '-'.
    return capitalize(formatter.of(toBcp47(code))) || fallback || code;
  } catch {
    return fallback || code;
  }
};

/**
 * @param {string} code código ISO 3166-1 alfa-2 (ex.: 'BR')
 * @param {string} [locale] idioma da interface (ex.: 'pt_BR')
 * @param {string} [fallback] nome canônico, usado se o código for desconhecido
 * @returns {string} nome do país no idioma pedido
 */
export const countryName = (code, locale, fallback) =>
  displayName('region', code, locale, fallback);

/**
 * @param {string} code código ISO 639-1 (ex.: 'pt')
 * @param {string} [locale] idioma da interface
 * @param {string} [fallback] nome canônico
 * @returns {string} nome do idioma no idioma pedido
 */
export const languageName = (code, locale, fallback) =>
  displayName('language', code, locale, fallback);

/**
 * Devolve a lista de países com `name` traduzido, preservando os demais campos
 * (`id`, `dial_code`, `emoji`) — inclusive o `id`, que é a chave usada para
 * gravar e para casar filtros salvos.
 *
 * @param {Array<{id: string, name: string}>} countries
 * @param {string} [locale]
 */
export const localizeCountries = (countries, locale) =>
  (countries || []).map(country => ({
    ...country,
    name: countryName(country.id, locale, country.name),
  }));

/**
 * Mesma ideia de {@link localizeCountries}, para a lista de idiomas.
 *
 * @param {Array<{id: string, name: string}>} languages
 * @param {string} [locale]
 */
export const localizeLanguages = (languages, locale) =>
  (languages || []).map(language => ({
    ...language,
    name: languageName(language.id, locale, language.name),
  }));

export default {
  countryName,
  languageName,
  localizeCountries,
  localizeLanguages,
};
